<template>
  <div class="variant-selector">

    <div class="variant-selector__groups" v-show="groupsHeader">

      <!-- variant groups header -->

      <strong class="variant-selector__groups__header" v-html="groupsHeader"></strong>

      <!-- variant groups -->

      <div class="variant-selector__groups__items">
        <button :key="item.id" v-for="item in props.variantGroups" :data-idvariantgroup="item.id" class="variant-selector__group" :class="[getClassesForVariantGroup(item), { 'variant-selector__group--selected': props.modelValue.selectedVariantGroup?.id === item.id }]" @click="variantGroupSelected(item.id)">

          <!-- sale label -->

          {{ variantGroupHasSaleLabel(item) ? $t('Akce') + ` -${getVariantGroupSale(item)}%` : '' }}

          <!-- variant group image -->

          <MoleculesGlobalWareImage :ware-name="props.wareName" :image-id="item.wareImage?.id ?? props.mainImageId" suffix="pl" class="w-full" :title="`${getCommonParameterValues(item)}${(selectedValue && !getVariantsForGroup(item).some(variant => variant.properties.some(property => property.values.some(value => value.lValue === selectedValue)))) ? ' - ' + $t('Není k dispozici pro ' + selectedValueText) : ''}`" />

          <!-- new color label -->

          {{ variantGroupHasNewColorLabel(item) ? $t('Nová barva') : '' }}
        </button>
      </div>
    </div>

    <div v-show="shownValues.length" class="variant-selector__values">

      <div class="tablet:flex gap-4 items-center">

        <!-- values header -->

        <strong class="block tracking-normal text-xl">{{ valuesHeader }}</strong>

        <!-- sizing table link -->

        <button v-if="props.hasSizingParameter" @click="emit('sizingTableOpened')" class="inline-block mt-1 font-bold text-sm text-neutral-400 underline">{{ $t('Jak vybrat správnou velikost?') }}</button>

      </div>

      <!-- values -->

      <div class="variant-selector__values__items">
        <button v-for="item in shownValues.sort((a, b) => b.priority - a.priority)" class="variant-selector__value" :class="{ 'variant-selector__value--selected': item.selected, 'variant-selector__value--not-stored': !item.stored }" @click="valueSelected(item.lValue)" :key="item.lValue">{{ item.value }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>

const props = defineProps({
  variants: Array,
  variantGroups: Array,
  mainImageId: Number,
  wareCreated: String, // For showing the "new color" badge
  hasNewColorBadge: Boolean,
  hasSizingParameter: Boolean,
  modelValue: Object // Selected variant
});

const emit = defineEmits(['update:modelValue', 'variantGroupNamesGenerated', 'parametersToBeSelectedUpdated', 'sizingTableOpened']);

const appConfig = useAppConfig();
const utils = useUtils();
const i18n = useI18n();
const business = useBusiness();
const cache = useCache();
const route = useRoute();

const now = new Date();

//#region variant groups block

const getVariantsForGroup = (variantGroup) => {
  return props.variants.filter(variant => variant.variantGroups.some(vg => vg.id === variantGroup?.id));
}

/**
 * Finds the intersection of parameters across multiple variant groups.
 * @param {Array} variantGroups - An array of variant groups.
 * @returns {Array} - An array of common parameter IDs.
 */
const getCommonParametersForVariantGroups = variantGroups => {

  let result = [];

  variantGroups.forEach((variantGroup, indexOfVariantGroup) => {

    const commonParameterIdsForVariantGroup = extractCommonParameterIdsFromVariants(getVariantsForGroup(variantGroup));

    // first variant group - use all its parameters
    if (indexOfVariantGroup === 0) {
      commonParameterIdsForVariantGroup.forEach(commonParameterId => result.push(commonParameterId));
    }

    // all other groups - remove parameters that do not match with the first variant group
    else {
      result = result.filter(q => commonParameterIdsForVariantGroup.includes(q));
    }
  });

  return result;
}

/**
 * Extracts the common parameter IDs from an array of variants.
 * 
 * @param {Array} variants - An array of variants.
 * @returns {Array} - An array of common parameter IDs.
 */
const extractCommonParameterIdsFromVariants = variants => {
  let commonValuesAndParametersForVariantGroup = [];

  variants.forEach((variant, indexOfVariant) => {

    // first variant within group - use all its parameters except size - it must be visible directly even if there is only one
    if (indexOfVariant === 0) {
      variant.properties.filter(variantProperty => variantProperty.id !== appConfig.parameterIds.size && variantProperty.id !== appConfig.parameterIds.size1).forEach(variantProperty => {
        variantProperty.values.forEach(variantPropertyValue => {
          commonValuesAndParametersForVariantGroup.push({
            parameterId: variantProperty.id,
            lValue: variantPropertyValue.lValue
          });
        });
      });
    }

    // all other variants within group - remove values and parameters that do not match with the first variant
    else {
      commonValuesAndParametersForVariantGroup = commonValuesAndParametersForVariantGroup.filter(item => {
        return variant.properties.some(q => item.parameterId === q.id && q.values.some(qq => item.lValue === qq.lValue));
      });
    }
  });

  const result = utils.removeDuplicatesFromArray(commonValuesAndParametersForVariantGroup.map(item => item.parameterId));

  return result;
}

/**
 * Computes the common parameter IDs based on the variant groups or variants.
 * If variant groups are present, it finds the common parameters among them.
 * If variants are defined without variant groups, it finds the common parameters among them.
 * If neither variant groups nor variants are present, it returns an empty array.
 *
 * @returns {Array} The common parameter IDs.
 */
const commonParameterIds = computed(() => {

  // if variant groups are present, find the common parameters among them
  if (props.variantGroups.length) {
    return getCommonParametersForVariantGroups(props.variantGroups);
  }

  // if variants are defined without variant groups, find the common parameters among them
  else if (props.variants.length > 1) {
    return extractCommonParameterIdsFromVariants(props.variants);
  }

  else {
    return [];
  }
});

/**
 * Computed property that returns the header text for the variant selector panel.
 * The header text is determined based on the presence of variant groups and the selected variant group.
 * If variant groups are present:
 *   - If there is only one common parameter (color), the header text will be "Vyberte barevnou variantu" if no variant group is selected,
 *     or "Barevná varianta - [selectedVariantGroup]" if a variant group is selected.
 *   - If there are multiple common parameters, the header text will be "Vyberte variantu" if no variant group is selected,
 *     or "Varianta - [commonValues]" if a variant group is selected and common parameter values are available.
 * @returns {string} The header text for the variant selector panel.
 */
const groupsHeader = computed(() => {
  let result;

  // if variant groups are present, show the header
  if (props.variantGroups.length) {

    // single parameter - color
    if (commonParameterIds.value?.length === 1 && commonParameterIds.value[0] === appConfig.parameterIds.color) {
      if (!props.modelValue.selectedVariantGroup) {
        result = i18n.t('Vyberte barevnou variantu');
      } else {
        result = i18n.t('Barevná varianta') + `<span>-</span><span class="variant-selector__groups__header__name">${getCommonParameterValues(props.modelValue.selectedVariantGroup)}</span>`;
      }

    }

    // label for generic variant
    else {
      if (!props.modelValue.selectedVariantGroup) {
        result = i18n.t('Vyberte variantu');
      } else {

        const commonValues = getCommonParameterValues(props.modelValue.selectedVariantGroup);

        if (commonValues) {
          result = i18n.t('Varianta') + `<span>-</span><span class="variant-selector__groups__header__name">${commonValues}</span>`;
        }
      }

    }
  }

  return result;
});

const getVariantGroupSale = variantGroup => {
  return getVariantsForGroup(variantGroup).map(variant => business.getVariantSalePercent(variant)).sort((a, b) => b - a)[0];
}

const allVariantGroupsAreOnSale = variantGroups => {
  return variantGroups.every(variantGroup => getVariantGroupSale(variantGroup) > 0);
}

const variantGroupSelected = (variantGroupId) => {
  // Find the selected variant group
  const _selectedVariantGroup = props.variantGroups.find(group => group.id === variantGroupId);

  if (!_selectedVariantGroup) {
    console.error(`Variant group with ID ${variantGroupId} not found`);
    return;
  }
  
  // If there is only one variant in the group, select it
  if (getVariantsForGroup(_selectedVariantGroup).length === 1) {
    emit('update:modelValue', {
      selectedVariant: props.variants.find(variant => variant.id === getVariantsForGroup(_selectedVariantGroup)[0].id),
      selectedVariantGroup: _selectedVariantGroup
    });
  }
  // Reselect value if selected previously so that the variant is updated
  else if (selectedValue.value) {
    valueSelected(selectedValue.value, _selectedVariantGroup);
  }
  else {
    emit('update:modelValue', {
      selectedVariant: null,
      selectedVariantGroup: _selectedVariantGroup
    });
  }

};

// Function to get the common parameter values for a variant group
const getCommonParameterValues = (variantGroup) => {
  // Initialize an array to store the common parameter values
  let commonParameterValues = [];

  // Iterate over each variant in the variant group
  getVariantsForGroup(variantGroup).forEach((variant, indexOfVariant) => {
    // Iterate over each property of the variant
    variant.properties.forEach(property => {
      // Check if the property id is included in the common parameters
      if (commonParameterIds.value.includes(property.id)) {
        // Iterate over each value of the property
        property.values.forEach(value => {
          // If it's the first variant, add all values to the commonParameterValues array
          if (indexOfVariant === 0) {
            commonParameterValues.push(value.value);
          } else {
            // If it's not the first variant, only keep the values that are also present in this variant
            if (!commonParameterValues.includes(value.value)) {
              const index = commonParameterValues.indexOf(value.value);
              // If the value is not present in this variant, remove it from the commonParameterValues array
              if (index > -1) {
                commonParameterValues.splice(index, 1);
              }
            }
          }
        });
      }
    });
  });

  const result = commonParameterValues.join(', ');

  return result;
}

const variantGroupHasSaleLabel = variantGroup => {
  return getVariantGroupSale(variantGroup) > 0 && !allVariantGroupsAreOnSale(props.variantGroups);
}

const anyOfVariantGroupsHaveSaleLabel = variantGroups => {
  return variantGroups.some(variantGroup => variantGroupHasSaleLabel(variantGroup));
}

const variantGroupHasNewColorLabel = variantGroup => {
  return props.hasNewColorBadge && variantGroup.createdAt && variantGroup.createdAt != props.wareCreated && utils.addDaysToDate(variantGroup.createdAt, appConfig.thresholdForNewColorBadgeInDays) >= now;
}

const anyOfVariantGroupsHasNewColorLabel = variantGroups => {
  return variantGroups.some(variantGroup => variantGroupHasNewColorLabel(variantGroup));
}

const getClassesForVariantGroup = variantGroup => {

  const hasSaleLabel = variantGroupHasSaleLabel(variantGroup);
  const hasNewColorLabel = variantGroupHasNewColorLabel(variantGroup);
  const othersHaveSaleLabel = anyOfVariantGroupsHaveSaleLabel(props.variantGroups);
  const othersHaveNewColorLabel = anyOfVariantGroupsHasNewColorLabel(props.variantGroups);

  return {
    'variant-selector__group--has-sale': hasSaleLabel,
    'variant-selector__group--has-new-color': hasNewColorLabel,
    'self-end': !hasSaleLabel && othersHaveSaleLabel,
    'self-start': !hasNewColorLabel && othersHaveNewColorLabel,
    'self-center': !hasSaleLabel && !hasNewColorLabel && othersHaveSaleLabel && othersHaveNewColorLabel,
  }
}

//#endregion

const allGroupVariantNames = computed(() => {
  const result = props.variantGroups.map(variantGroup => {

    // Sort the variants so that the one with the selected value comes first
    const sortedVariants = getVariantsForGroup(variantGroup).sort((a, b) => {
      const aValue = a.properties.find(property => property.values.some(value => value.lValue === selectedValue.value));
      const bValue = b.properties.find(property => property.values.some(value => value.lValue === selectedValue.value));
      return (bValue ? 1 : 0) - (aValue ? 1 : 0);
    });

    return {
      variantGroupId: variantGroup.id,
      name: getCommonParameterValues(variantGroup),
      defaultVariantId: sortedVariants?.[0]?.id
    }
  });

  return result;
});

//#region parameter values block

const getAllValues = variants => {

  let allValues = [];

  if (variants?.length === 1 && variants[0].properties?.length) {

    const parameterOtherThanColor = variants[0].properties.find(property => property.id !== appConfig.parameterIds.color);

    if (parameterOtherThanColor) {
      allValues = parameterOtherThanColor?.values?.map(value => {
        return {
          propertyId: parameterOtherThanColor.id,
          value: value.value,
          lValue: value.lValue,
          priority: value.priority,
          stored: variants[0].stored,
          selected: selectedValue.value === value.lValue && (!props.variantGroups?.length || props.variantGroups.length === 1)
        }
      });
    }

  }
  else {
    variants.forEach(variant => {
      variant.properties.forEach(property => {
        if (!commonParameterIds.value.includes(property.id)) {
          property.values.forEach(value => {
            if (!allValues.some(q => q.lValue === value.lValue)) {
              allValues.push({
                propertyId: property.id,
                value: value.value,
                lValue: value.lValue,
                priority: value.priority,
                stored: variants.some(q => q.stored && q.properties.some(qq => qq.values.some(qqq => qqq.lValue === value.lValue))),
                selected: selectedValue.value === value.lValue && (!props.variantGroups?.length || props.variantGroups.length === 1)
              });
            }
          });
        }
      });
    });
  }

  if (allValues.length === 1 && allValues[0].propertyId === appConfig.parameterIds.color) {
    allValues = [];
  }

  return allValues;
};

const valueSelected = (lValue, newVariantGroup) => {
  let variant, variantGroup;

  // variant group has been switched, try to find the variant in the new group and also update the selected variant group
  if (newVariantGroup)
  {
    variant = props.variants.find(variantInGroup => variantInGroup.id === getVariantsForGroup(newVariantGroup).find(variant => variant.properties.some(property => property.values.some(value => value.lValue === lValue)))?.id);
    variantGroup = newVariantGroup;
  }
  // value has been selected within the same variant group
  else if (props.modelValue.selectedVariantGroup) {
    variant = props.variants.find(variantInGroup => variantInGroup.id === getVariantsForGroup(props.modelValue.selectedVariantGroup)?.find(variant => variant.properties.some(property => property.values.some(value => value.lValue === lValue)))?.id);
    variantGroup = props.modelValue.selectedVariantGroup;
  }
  // no variant group has been selected but they exist, select the first one
  else if (props.variantGroups.length) {
    variant = props.variants.find(variant => variant.properties.some(property => property.values.some(value => value.lValue === lValue)));
    variantGroup = props.variantGroups[0];
  }
  // select variant and ignore variant groups
  else {
    variant = props.variants.find(variant => variant.properties.some(property => property.values.some(value => value.lValue === lValue)));
    variantGroup = null;
  }

  emit('variantGroupNamesGenerated', allGroupVariantNames.value);

  emit('update:modelValue', {
    selectedVariant: variant,
    selectedVariantGroup: variantGroup
  });
};

const valuesHeader = computed(() => {

  const otherParameterThanColor = props.variants[0]?.properties.find(property => property.id !== appConfig.parameterIds.color);

  let result = otherParameterThanColor?.name ?? '';

  const overrideText = otherParameterThanColor && appConfig.parameterNameHeadingOverrides.find(override => override.parameterId === otherParameterThanColor.id)?.text;

  if (overrideText) {
    result = i18n.t(overrideText);
  }
  else if (result) {
    result = result.indexOf(':') > -1 ? result.split(':')[1] : result;
  }

  return result;
});

const shownValues = computed(() => {

  let result;

  if (props.modelValue.selectedVariantGroup) {

    // Extract the parameter values from all variants, excluding the common parameters
    result = getVariantsForGroup(props.modelValue.selectedVariantGroup).flatMap(variant =>
      variant.properties.flatMap(property => {
        // Skip the common parameters
        if (commonParameterIds.value.includes(property.id)) {
          return [];
        }

        return property.values.map(value => { return { value: value.value, lValue: value.lValue, priority: value.priority, stored: variant.stored, selected: selectedValue.value === value.lValue }; });
      })
    );
  }
  else {
    result = getAllValues(props.variants);
  }

  return result;
});

const selectedValue = computed(() => {
  const selectedVariant = props.modelValue.selectedVariant;
  const result = selectedVariant?.properties?.find(property => property.id !== appConfig.parameterIds.color)?.values?.find(value => value.lValue === selectedVariant?.properties?.find(property => property.id !== appConfig.parameterIds.color)?.values?.[0]?.lValue)?.lValue;

  return result;
});

const selectedValueText = computed(() => {
  const selectedVariant = props.modelValue.selectedVariant;
  const result = selectedVariant?.properties?.find(property => property.id !== appConfig.parameterIds.color)?.values?.find(value => value.lValue === selectedVariant?.properties?.find(property => property.id !== appConfig.parameterIds.color)?.values?.[0]?.lValue)?.value;

  return result;
});

//#endregion

const parameterNamesToBeSelected = computed(() => {

  const result = [];
  const variantGroupParameterNames = [];

  if (props.variantGroups.length) {
    const commonParameterIdsForVariantGroups = getCommonParametersForVariantGroups(props.variantGroups);

    commonParameterIdsForVariantGroups.forEach(commonParameterId => {
      const parameter = getVariantsForGroup(props.variantGroups[0])[0].properties.find(property => property.id === commonParameterId);
      if (parameter) {
        variantGroupParameterNames.push(parameter.name);
      }
    });
  }

  if (!props.modelValue.selectedVariantGroup) {
    variantGroupParameterNames.forEach(parameterName => {
      if (!result.includes(parameterName)) {
        result.push(parameterName.indexOf(':') > -1 ? parameterName.split(':')[1] : parameterName);
      }
    });
  }

  if (!props.modelValue.selectedVariant) {

    props.variants[0].properties.forEach(parameter => {
      if (!result.includes(parameter.name) && !variantGroupParameterNames.includes(parameter.name)) {
        result.push(parameter.name.indexOf(':') > -1 ? parameter.name.split(':')[1] : parameter.name);
      }
    });
  }

  return result;
});

watch(() => parameterNamesToBeSelected.value, (newValue) => {
  emit('parametersToBeSelectedUpdated', newValue);
}, { immediate: true });

watch(() => props.modelValue.selectedVariant, (newValue, oldValue) => {

  // find variant group based on variant
  if (newValue?.id && newValue.id !== oldValue?.id && props.variantGroups.length) {

    const newSelectedVariantGroup = props.variantGroups.find(group => getVariantsForGroup(group).some(variant => variant.id === newValue.id));

    if (newSelectedVariantGroup?.id !== props.modelValue.selectedVariantGroup?.id) {
      emit('update:modelValue', {
        selectedVariant: newValue,
        selectedVariantGroup: newSelectedVariantGroup
      });
    }
  }

}, { deep: true });

const updateVariantFromHash = () => {

  const hash = route.hash.substring(1); // Remove the '#' at the start

  if (hash) {
    const newVariant = props.variants.find(variant => variant.id.toString() === hash);

    emit('update:modelValue', {
      selectedVariant: newVariant,
      selectedVariantGroup: props.modelValue.selectedVariantGroup
    });
  }
}

// Update selectedVariant when the route changes
watch(() => route.hash, (newHash, oldHash) => {
  updateVariantFromHash();
}, { immediate: route.hash });

// if there is only one variant and none is selected, select it
if (!props.modelValue.selectedVariant && props.variants.length === 1) {
  emit('update:modelValue', {
    selectedVariant: props.variants[0],
    selectedVariantGroup: props.modelValue.selectedVariantGroup
  });
}
else if (!props.modelValue.selectedVariantGroup && props.variantGroups.length === 1) {
  variantGroupSelected(props.variantGroups[0].id);
}

// Get the common parameter values for all variant groups
emit('variantGroupNamesGenerated', allGroupVariantNames.value);


</script>

<style lang="postcss">
.variant-selector {

  &__groups {

    &__header {
      @apply flex gap-1 mb-4 tracking-normal text-xl tablet:gap-3;

      &__name {
        @apply uppercase text-secondary-500;
      }
    }

    &__items {
      @apply flex flex-wrap items-center;
    }
  }

  &__group {
    @apply p-[5px] bg-neutral-50 transition-colors duration-300 ease-in-out;

    &--has-new-color,
    &--has-sale {
      @apply uppercase font-bold text-xs text-neutral-50;
    }

    &--has-new-color {
      @apply pb-[8px] mb-2 bg-[#CB7323];

      img {
        @apply mb-[8px];
      }
    }

    &--has-sale {
      @apply pt-[8px] mt-2 bg-[#D13E48];

      img {
        @apply mt-[8px];
      }
    }

    &--has-new-color&--has-sale {
      @apply bg-gradient-to-t from-[#CB7323] to-[#D13E48];
    }

    &--selected {
      @apply bg-secondary-500 !from-secondary-500 !to-secondary-500;
    }
  }

  &__values {

    &__items {
      @apply flex flex-wrap items-center mt-1;
    }
  }

  &__value {

    @apply inline-block px-2 py-1 mr-[5px] mb-[5px] border border-transparent bg-neutral-500 text-neutral-50 cursor-pointer transition-colors duration-300 ease-in-out;

    &--not-stored {
      @apply border border-neutral-300 bg-transparent text-neutral-300;
    }

    &--selected {
      @apply bg-secondary-500 border-secondary-500 text-white;
    }
  }
}
</style>